<template>
  <div class="mt-2 mb-4 ospe-sub-specialisations-manager">
    <label class="form-label">Sub-specialisations</label>
    <div
      class="table-responsive"
    >
      <table class="table table-striped table-bordered table-hover table-sm resources-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Name (Français)</th>
            <th class="tight text-center">Enabled</th>
            <th class="table-actions" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="subSpecialisation in subSpecialisations"
            :key="itemKey(subSpecialisation)"
            :class="{ 'table-danger': subSpecialisation.rm }"
          >
            <td>{{ subSpecialisation.name }}</td>
            <td>{{ subSpecialisation.nameFr }}</td>
            <td class="tight text-center">
              {{ subSpecialisation.enabled ? "Yes" : "No" }}
            </td>
            <td class="table-actions">
              <button
                v-if="!subSpecialisation.rm"
                type="button"
                class="btn btn-sm btn-success me-1"
                :aria-label="`Edit ${subSpecialisation.name}`"
                @click="editSubSpecialisation(subSpecialisation)"
              >
                Edit
              </button>
              <button
                v-if="!subSpecialisation.rm"
                type="button"
                class="btn btn-sm btn-danger"
                :aria-label="`Delete ${subSpecialisation.name}`"
                @click="deleteSubSpecialisation(subSpecialisation)"
              >
                Delete
              </button>
              <button
                v-else
                type="button"
                class="btn btn-sm btn-info"
                :aria-label="`Restore ${subSpecialisation.name}`"
                @click="restoreSubSpecialisation(subSpecialisation)"
              >
                Restore
              </button>
            </td>
          </tr>
          <tr data-system-sub-specialisation="other">
            <td>Other</td>
            <td>Autre</td>
            <td class="tight text-center">Yes</td>
            <td class="table-actions" />
          </tr>
        </tbody>
      </table>
    </div>
    <div class="d-flex align-items-center flex-wrap">
      <button
        type="button"
        class="btn btn-sm btn-success table-action-size me-2"
        @click="addSubSpecialisation"
      >
        Add sub-specialisation
      </button>
      <small
        v-if="hasChanges"
        class="ms-auto text-end text-info"
      >
        Changes to sub-specialisations will be applied when the specialisation is saved.
      </small>
    </div>

    <teleport to="#vue-portal-target">
      <ts-modal
        ref="modal"
        :modal-title="modalTitle"
        modal-class="ospe-sub-specialisation-modal"
        @modal-shown="focusNameInput"
      >
        <template #modal-content>
          <div class="row gx-2">
            <div class="mb-3 col-sm-6">
              <label class="form-label" for="ospe_sub_specialisation_name">Name</label>
              <input
                id="ospe_sub_specialisation_name"
                ref="nameInput"
                v-model="modalData.name"
                type="text"
                :class="['form-control', { 'is-invalid': nameError }]"
                @input="nameError = null"
                @keyup.enter="saveSubSpecialisation"
              >
              <div class="invalid-feedback">
                {{ nameError }}
              </div>
            </div>
            <div class="mb-3 col-sm-6">
              <label class="form-label" for="ospe_sub_specialisation_name_fr">Name (Français)</label>
              <input
                id="ospe_sub_specialisation_name_fr"
                v-model="modalData.nameFr"
                type="text"
                :class="['form-control', { 'is-invalid': nameFrError }]"
                @input="nameFrError = null"
                @keyup.enter="saveSubSpecialisation"
              >
              <div class="invalid-feedback">
                {{ nameFrError }}
              </div>
            </div>
          </div>
          <div class="form-check">
            <input
              id="ospe_sub_specialisation_enabled"
              v-model="modalData.enabled"
              type="checkbox"
              class="form-check-input"
            >
            <label
              for="ospe_sub_specialisation_enabled"
              class="form-check-label"
            >
              Enabled
            </label>
          </div>
        </template>
        <template #modal-footer>
          <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            @click="saveSubSpecialisation"
          >
            {{ actionTitle }}
          </button>
        </template>
      </ts-modal>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue"

import TsModal from "@/js/vue-components/ts-modal.vue"
import type { SubSpecialisation } from "@/js/vue-components/ospe/sub-specialisation"

const props = defineProps<{
  initialValue: SubSpecialisation[]
}>()

const emit = defineEmits<{
  "change": [value: SubSpecialisation[]]
}>()

const modal = useTemplateRef<InstanceType<typeof TsModal>>("modal")
const nameInput = useTemplateRef<HTMLInputElement>("nameInput")
const initialValueJson = JSON.stringify(props.initialValue)
const subSpecialisations = ref(props.initialValue)
const editingIndex = ref<number | null>(null)
const nameError = ref<string | null>(null)
const nameFrError = ref<string | null>(null)
const modalData = ref({
  name: "",
  nameFr: "",
  enabled: true,
})
const itemKeys = new WeakMap<SubSpecialisation, string>()
let nextItemKey = 0

const hasChanges = computed(() => {
  return JSON.stringify(subSpecialisations.value) !== initialValueJson
})

const modalTitle = computed(() => {
  return editingIndex.value !== null ? "Edit sub-specialisation" : "Add sub-specialisation"
})

const actionTitle = computed(() => {
  return editingIndex.value !== null ? "Update" : "Add"
})

const itemKey = (subSpecialisation: SubSpecialisation): string => {
  let key = itemKeys.get(subSpecialisation)
  if (!key) {
    nextItemKey += 1
    key = `sub-specialisation-${nextItemKey}`
    itemKeys.set(subSpecialisation, key)
  }

  return key
}

const updateSubSpecialisations = (value: SubSpecialisation[]): void => {
  subSpecialisations.value = value
  emit("change", value)
}

const resetModal = (): void => {
  nameError.value = null
  nameFrError.value = null
  modalData.value = {
    name: "",
    nameFr: "",
    enabled: true,
  }
}

const addSubSpecialisation = (): void => {
  editingIndex.value = null
  resetModal()
  modal.value?.show()
}

const editSubSpecialisation = (subSpecialisation: SubSpecialisation): void => {
  editingIndex.value = subSpecialisations.value.indexOf(subSpecialisation)
  nameError.value = null
  nameFrError.value = null
  modalData.value = {
    name: subSpecialisation.name,
    nameFr: subSpecialisation.nameFr,
    enabled: subSpecialisation.enabled,
  }
  modal.value?.show()
}

const saveSubSpecialisation = (): void => {
  const name = modalData.value.name.trim()
  const nameFr = modalData.value.nameFr.trim()
  const reservedNameMessage = 'Other and Autre are reserved for the system Other option.'
  nameError.value = !name ? 'Name is required.'
    : ['other', 'autre'].includes(name.toLowerCase()) ? reservedNameMessage : null
  nameFrError.value = ['other', 'autre'].includes(nameFr.toLowerCase()) ? reservedNameMessage : null
  if (nameError.value || nameFrError.value) {
    nameInput.value?.focus()
    return
  }

  const attributes = {
    name,
    nameFr,
    enabled: modalData.value.enabled,
  }
  if (editingIndex.value !== null) {
    updateSubSpecialisations(subSpecialisations.value.map((item, index) => (
      index === editingIndex.value ? { ...item, ...attributes } : item
    )))
  } else {
    updateSubSpecialisations([...subSpecialisations.value, {
      id: null,
      rm: false,
      ...attributes,
    }])
  }

  modal.value?.hide()
}

const deleteSubSpecialisation = (subSpecialisation: SubSpecialisation): void => {
  if (!window.confirm(`Delete sub-specialisation "${subSpecialisation.name}"?`)) return

  updateSubSpecialisations(subSpecialisation.id === null
    ? subSpecialisations.value.filter((item) => item !== subSpecialisation)
    : subSpecialisations.value.map((item) => (
      item === subSpecialisation ? { ...item, rm: true } : item
    )))
}

const restoreSubSpecialisation = (subSpecialisation: SubSpecialisation): void => {
  updateSubSpecialisations(subSpecialisations.value.map((item) => (
    item === subSpecialisation ? { ...item, rm: false } : item
  )))
}

const focusNameInput = (): void => {
  nameInput.value?.focus()
}
</script>

<style scoped>
.table-action-size {
  padding: 0 0.5rem;
}
</style>
